import type { MenuDefinition, MenuEntry, MenuTab } from '@/shared/layouts/menu/types';
import { keilogComingSoonPath } from '../constants/paths';
import type {
  KeilogMenuGroup,
  KeilogMenuItem,
  RbacMenuEntry,
  RbacMenuGroup,
  RbacMenuTab,
} from '../types/permissions';
import { KEILOG_MENU_ROUTES } from './screenRegistry';

/**
 * The KEiLOG sidebar, drawn from the menu `/me/permissions` returns (schema 3).
 *
 * The server selects the active role's concept layout and keeps only authorized entries.
 * This adapter preserves that layout, including the practitioner's single untitled tab.
 *
 * What it adds is presentation:
 * - the tooltip (`full`, the concept's `full || name`);
 * - 準備中 for an entry without a screen (`status: pending`, or a path no route answers);
 * - a one-item group (`direct`, 返戻過誤) drawn as a direct link (MN-08).
 *
 * The dashboard is not a menu entry: the logo opens it (`RahSidebar`, HOME-2026-09-26).
 */

const routeOf = (path: string): string => path.split('#')[0] ?? '';

const REGISTERED_ROUTES: ReadonlySet<string> = new Set(KEILOG_MENU_ROUTES.map(route => route.path));

/** Whether a route of this bundle answers the path (hash ignored). */
const isRegisteredPath = (path: string): boolean => REGISTERED_ROUTES.has(routeOf(path));

/** The dashboard (`/`) is opened by the logo, never listed (D15). */
const isDashboardPath = (path: string | undefined): boolean => path !== undefined && routeOf(path) === '/';

/** Entries already reported, so a re-render does not repeat the warning. */
const reportedEntries = new Set<string>();

/**
 * An entry the grants show but no route of this bundle answers. It stays in the menu, drawn as
 * 準備中, rather than vanish: a grant that shows nothing reads as a grant that does nothing.
 * Reported once so the drift between `Config/rbac.php` and `screenRegistry.ts` is found.
 */
const reportUnroutedEntry = (entry: RbacMenuEntry): void => {
  const key = `${entry.id} ${entry.path ?? ''}`;
  if (reportedEntries.has(key)) return;
  reportedEntries.add(key);
  console.warn(
    `[keilog menu] "${entry.id}" (${entry.path ?? 'no path'}) has no route in screenRegistry; drawn as 準備中.`
  );
};

/** A built (or partly built) entry that names where it goes. */
const opensRoute = (entry: RbacMenuEntry): boolean =>
  entry.status !== 'pending' && !entry.disabled && Boolean(entry.path);

/** Ids of the built entries no registered route answers: the ones drawn 準備中 for that reason. */
export function unroutedMenuEntryIds(tabs: readonly RbacMenuTab[]): string[] {
  return tabs.flatMap(tab =>
    tab.groups.flatMap(group =>
      group.items
        .filter(item => !item.section && opensRoute(item) && !isRegisteredPath(item.path ?? ''))
        .map(item => item.id)
    )
  );
}

/** Whether the entry is drawn as 準備中: no screen yet, or no route answers its path. */
const isPending = (entry: RbacMenuEntry): boolean => {
  if (entry.status === 'pending' || !entry.path) return true;
  if (isRegisteredPath(entry.path)) return false;
  reportUnroutedEntry(entry);
  return true;
};

const toMenuItem = (entry: RbacMenuEntry): KeilogMenuItem => {
  const pending = isPending(entry);

  return {
    label: entry.label,
    // A 準備中 entry is never a link; the placeholder path only keeps the core model whole.
    path: pending ? keilogComingSoonPath(entry.id) : (entry.path ?? ''),
    ...(entry.icon ? { icon: entry.icon } : {}),
    ...(entry.tag ? { tag: entry.tag } : {}),
    ...(entry.full ? { full: entry.full } : {}),
    ...(pending ? { pending: true, disabled: true } : {}),
    ...(!pending && entry.disabled ? { disabled: true } : {}),
  };
};

/** Section headings that keep at least one item below them. */
const withoutEmptySections = (entries: MenuEntry[]): MenuEntry[] =>
  entries.filter((entry, index) => {
    if (!('section' in entry)) return true;
    const next = entries[index + 1];
    return next !== undefined && !('section' in next);
  });

/**
 * A group the API marks `direct`, or a schema-2 group that is its own destination: one link
 * on the rail, no flyout (MN-08).
 */
const toDirectGroup = (group: RbacMenuGroup): KeilogMenuGroup => {
  const target = group.items.find(item => item.id === group.direct) ?? group;
  const pending = isPending(target);

  return {
    id: group.id,
    label: group.label,
    icon: group.icon ?? '',
    path: pending ? keilogComingSoonPath(group.id) : (target.path ?? ''),
    ...(target.full ? { full: target.full } : {}),
    ...(pending ? { pending: true, disabled: true } : {}),
  };
};

const toMenuGroup = (group: RbacMenuGroup): KeilogMenuGroup | null => {
  if (group.direct || (group.path && group.items.length === 0)) {
    return isDashboardPath(group.path) ? null : toDirectGroup(group);
  }

  const items = withoutEmptySections(
    group.items
      .filter(entry => !isDashboardPath(entry.path))
      .map((entry): MenuEntry => (entry.section ? { section: entry.section } : toMenuItem(entry)))
  );
  if (!items.some(entry => !('section' in entry))) return null;

  return {
    id: group.id,
    label: group.label,
    icon: group.icon ?? '',
    ...(group.grpTabs ? { grpTabs: true } : {}),
    items,
  };
};

const toMenuTab = (tab: RbacMenuTab): MenuTab => ({
  id: tab.id,
  label: tab.label,
  groups: tab.groups.flatMap(group => {
    const projected = toMenuGroup(group);
    return projected ? [projected] : [];
  }),
});

/**
 * The sidebar model of the server's menu tree, in the server's order. Empty groups and tabs are
 * dropped; nothing else is.
 */
export function projectKeilogMenu(tabs: readonly RbacMenuTab[]): MenuDefinition {
  return {
    tabs: tabs.map(toMenuTab).filter(tab => tab.groups.length > 0),
  };
}

/** Test seam: forget which unrouted entries were reported. */
export function resetUnroutedMenuReports(): void {
  reportedEntries.clear();
}
